import { Globals } from '../../modules/Globals';
import axios from 'axios';
import {
  PaymentPlatform,
  PaymentStatus,
  PaymentType,
} from '../../enums/PaymentEnums';
import { IPayment } from '../../interfaces/IPayment';
import { IPaginated } from '../../interfaces/IPaginated';
import { IOrder } from '../../interfaces/IOrder';
import { PaymentEntityType } from '@/enums/PaymentEntityType';
import { IDataOptions } from '@/interfaces/IDataOptions';
import { serializeQuery } from '@/utils';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { IAuthData } from '@/interfaces/IAuthData';
import { useSocketStore } from '@/composition/use-socket';
import { useOfflineStore } from '@/stores/offline.store';

const apiUrl = Globals.apiPath + '/payments';
const v2ApiUrl = Globals.apiPath + '/v2/payments';

export interface ICreatePaymentDto {
  platform: PaymentPlatform | null;
  userId: number;
  restaurantId: number;
  branchId: number;
  total: number;
  comment?: string | null;
  type: PaymentType;
  entityType: PaymentEntityType;
  entityIds: number[];
  paymentMethod: PaymentMethod;
  cajaId?: number | null;
  customerId?: number | null;
  clubMemberId?: number | null;
  /** Live member code, consumed server-side when paying with club balance. */
  clubCode?: string | null;
}

export interface IPaymentStatus {
  status: PaymentStatus;
}

export default class PaymentsService {
  constructor() {}

  async getAll(options = null): Promise<IPaginated<IPayment>> {
    let path = `${apiUrl}?a=1`;

    if (options) path += `&options=${JSON.stringify(options)}`;

    let res = await axios.get<IPaginated<IPayment>>(path);
    return res.data;
  }

  async checkMercadoPagoPayment(preferenceId: string, orderId: string) {
    let res = await axios.get<IPaymentStatus>(
      `${apiUrl}/checkMp?preferenceId=${preferenceId}&orderId=${orderId}`
    );
    return res.data;
  }

  async checkPaypalPayment(paymentId: string) {
    let res = await axios.get<IPaymentStatus>(
      `${apiUrl}/checkPaypal?paymentId=${paymentId}`
    );
    return res.data;
  }

  // V2

  /**
   * Retrieves a paginated list of payments with optional filtering
   */
  static async v2FindMany(
    where: any,
    dataOptions?: IDataOptions,
    onlyPending: boolean = false
  ): Promise<IPaginated<IPayment>> {
    const res = await axios.get<IPaginated<IPayment>>(
      `${v2ApiUrl}?${serializeQuery({
        dataOptions: dataOptions ? JSON.stringify(dataOptions) : undefined,
        onlyPending,
        r: where.restaurantId,
        b: where.branchId,
      })}`
    );
    return res.data;
  }

  /**
   * Finds payments by their reference entity type and ID
   */
  static async v2FindByReference(
    entityType: PaymentEntityType,
    entityId: number,
    dataOptions?: IDataOptions
  ): Promise<IPaginated<IPayment>> {
    const res = await axios.get<IPaginated<IPayment>>(
      `${v2ApiUrl}/ref/${entityType}/${entityId}?${serializeQuery({
        dataOptions: dataOptions ? JSON.stringify(dataOptions) : undefined,
      })}`
    );
    return res.data;
  }

  static async v2SetStatus(
    paymentId: number,
    status: PaymentStatus,
    authData?: IAuthData
  ) {
    const res = await axios.put(`${v2ApiUrl}/${paymentId}/status`, {
      status,
      authData,
    });
    return res.data;
  }

  static async v2Create(dto: ICreatePaymentDto) {
    const socket = useSocketStore();
    if (!socket.isConnected && Globals.offlineOrderingEnabled) {
      return (useOfflineStore() as any).offlineCreatePayment(dto);
    }

    const res = await axios.post(`${v2ApiUrl}`, dto);
    return res.data;
  }

  static async v2ChangePaymentMethod(
    id: number,
    paymentMethod: PaymentMethod,
    authData?: IAuthData
  ) {
    const res = await axios.put(`${v2ApiUrl}/${id}/payment-method`, {
      paymentMethod,
      authData,
    });
    return res.data;
  }
}
